import axios from "axios";
import { useEffect, useState } from "react";
import { ArrowButton } from "../../../../ui/General/ArrowButton/ArrowButton";
import { AccompanyingItem as AccompanyingItemType } from "./AccompanyingItem.interface";
import { AccompanyingItem } from "./AccompanyingItem";
import { AccompanyingItemsTotalBox } from "./AccompanyingItemsTotalBox";
import { AccompanyingItemsSelectedItems } from "./AccompanyingItemsSelectedItems";
import { useLocalStorage } from "../../../../hooks/useLocalStorage";
import { API_URL } from "../../../../config.tsx";
import { RootState, useAppSelector } from "@/utils/storage/stores/store.ts";

interface CartProduct {
    productId: number;
}

interface Props {
    currentItem: AccompanyingItemType;
    currentBranchId: number;
    productId: number;
    brandId: string;
    ssd: string;
    isCartProduct: boolean;
}

/**
 * Блок "Сопутствующие товары"
 * @returns
 */
export const ProductAccompanyingItems: React.FC<Props> = ({
    currentItem,
    currentBranchId,
    productId,
    brandId,
    ssd,
    isCartProduct
}) => {
    const cartId = useAppSelector((state: RootState) => state.user.userInfo?.cartId as number);
    const [accompanyingItems, setAccompanyingItems] = useState<AccompanyingItemType[]>(currentItem ? [currentItem] : []);
    const [isOpen, setIsOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [dots, setDots] = useState(0);
    const [selectedItems, setSelectedItems] = useState<AccompanyingItemType[]>([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const { getStorageValue } = useLocalStorage();
    const cartProducts = getStorageValue('cartOffer')?.products || [];

    //Загрузка сопутствующих товаров
    useEffect(() => {
        setIsLoading(true);
        axios.get(`${API_URL}/accompanying_items/${currentBranchId}/${productId}?catalog=${brandId}&ssd=${ssd}`, { withCredentials: true })
            .then(response => response.data)
            .then(data => setAccompanyingItems([currentItem, ...data]))
            .finally(() => setIsLoading(false));
    }, []);

    //Обновление списка сопутствующих товаров после обновления данных по выброанному товару
    useEffect(() => {
        setSelectedItems(prev => {
            const updatedItems = [...prev.filter(item => item.productId !== currentItem.productId)];
            return !isCartProduct ? [currentItem, ...updatedItems] : updatedItems;
        });
        setAccompanyingItems(prev => [currentItem, ...prev.slice(1)]);
    }, [currentItem]);

    //Индикатор загрузки
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isLoading) {
            interval = setInterval(() => {
                setDots(prev => (prev + 1) % 4);
            }, 500);
        }

        return () => clearInterval(interval);
    }, [isLoading]);

    //Обновление общей стоимости комплекта
    useEffect(() => {
        setTotalPrice(selectedItems.reduce((acc, item) => acc + item.price, 0));
    }, [selectedItems]);

    if (isLoading) return <div><p>Загружаем информацию о сопутствующих товарах{Array.from({ length: dots }).map((_, index) => <span key={`dot-${index}`}>.</span>)}</p></div>;

    return !!accompanyingItems.length &&
        <div className="w-full max-w-5xl mb-8">
            <button onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center px-0 py-2 font-bold text-base text-left hover:text-orange-600 transition mb-3 cursor-">
                <span>{`Есть сопутствующие товары под ваш автомобиль (${accompanyingItems.length})`}</span>
                <span className='ml-2'><ArrowButton state={isOpen ? 'up' : 'down'} /></span>
            </button>
            {isOpen && <div className="border rounded-xl bg-white p-3 mb-3">
                {/* <div className="flex items-center justify-between mb-3 border-b border-gray-200 pb-2">
                    <h4 className="font-bold text-lg text-gray-800">КОМПЛЕКТ ДЕШЕВЛЕ</h4>
                    <p className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">СКИДКА 15%</p>
                </div> */}
                <div className="flex items-stretch flex-col sm:flex-row gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                    <AccompanyingItemsSelectedItems selectedItems={selectedItems} />
                    <AccompanyingItemsTotalBox
                        totalPrice={totalPrice}
                        selectedItems={!isCartProduct ? selectedItems : selectedItems.filter(item => item.productId !== currentItem.productId)}
                        cartId={cartId}
                        isCurrentInCart={isCartProduct} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {accompanyingItems.map(item =>
                        <AccompanyingItem key={item.id}
                            item={item}
                            isCurrent={item.id === currentItem.id}
                            isCartProduct={cartProducts.some((cartProduct: CartProduct) =>
                                cartProduct.productId === item.productId)}
                            isSelected={selectedItems.some(selectedItem => selectedItem.id === item.id)}
                            onSelect={() => setSelectedItems(prev => [...prev, item])}
                            onRemove={() => setSelectedItems(prev => prev.filter(selectedItem => selectedItem.id !== item.id))} />)}
                </div>
            </div>}
        </div>
};
